import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useStorage } from '../../../../hooks/useStorage';
import { AthleteSession, AthleteWithTeam } from '../types';

export interface TabletContextValue {
  // SelectedAthlete are athletes that are selected to be displayed on the tablet
  selectedAthletes: AthleteWithTeam[];
  removeAthleteFromSelectedAthletes: (athleteId: string) => void;

  // ActiveAthletes are athletes that are currently shown on the tablet
  activeAthletes: AthleteWithTeam[];
  activeAthleteIds: string[];
  setActiveAthletes: (athletes: AthleteWithTeam[]) => void;
  isAthleteActive: (athleteId: string) => boolean;

  // AthleteSession holds the workout and section information for an athlete
  athleteSession: Record<string, AthleteSession>;
  getSelectedSectionId: (athleteId: string) => string | undefined;
  getSelectedWorkoutId: (athleteId: string) => string | undefined;
  onSelectWorkout: (
    workoutId: string | undefined,
    workoutName: string | undefined,
    athleteId: string,
  ) => void;
  onSelectSection: (sectionId: string | undefined, athleteId: string) => void;

  // View state
  splitViewEnabled: boolean;
  canShowSplitView: boolean;
  onSetSplitView: (enabled: boolean) => void;

  // Exit the tablet mode
  onExit: () => void;
}

const TabletContext = createContext<TabletContextValue | undefined>(undefined);
export interface TabletProviderProps {
  children: React.ReactNode;
  selectedAthletes: AthleteWithTeam[];
  deselectAthlete: (athleteId: string) => void;
  onExit: () => void;
}

export const TabletProvider: React.FC<TabletProviderProps> = ({
  children,
  selectedAthletes,
  deselectAthlete,
  onExit,
}) => {
  const [activeAthletes, setActiveAthletes] = useStorage<AthleteWithTeam[]>(
    'tabletActiveAthletes',
    [],
  );
  const [athleteSession, setAthleteSession] = useStorage<
    Record<string, AthleteSession>
  >('tabletSessions', {});

  const [splitViewEnabled, setSplitViewEnabled] = useStorage<boolean>(
    'tabletSplitViewEnabled',
    false,
  );

  const canShowSplitView = useMemo(
    () => selectedAthletes.length > 1 && selectedAthletes.length <= 4,
    [selectedAthletes.length],
  );

  // Auto-select athletes when the list changes, preserving valid selections
  useEffect(() => {
    const selectedAthleteIds = selectedAthletes.map((a) => a.id);
    const areCurrentSelectionsValid = activeAthletes.every((athlete) =>
      selectedAthleteIds.includes(athlete.id)
    );

    // Only auto-select if no active athletes OR current active athletes are invalid
    if (activeAthletes.length === 0 || !areCurrentSelectionsValid) {
      if (splitViewEnabled && canShowSplitView) {
        setActiveAthletes(selectedAthletes);
      } else if (selectedAthletes[0]) {
        setActiveAthletes([selectedAthletes[0]]);
      } else {
        setActiveAthletes([]); // Clear stale selection
      }
    }
  }, [selectedAthletes, activeAthletes, splitViewEnabled, canShowSplitView, setActiveAthletes]);

  const activeAthleteIds = useMemo(
    () => activeAthletes.map((athlete) => athlete.id),
    [activeAthletes],
  );

  const onSelectWorkout = useCallback(
    (
      workoutId: string | undefined,
      workoutName: string | undefined,
      athleteId: string,
    ) => {
      setAthleteSession((prev) => ({
        ...prev,
        [athleteId]: {
          workoutId,
          workoutName,
          sectionId: undefined,
        },
      }));
    },
    [setAthleteSession],
  );

  const onSelectSection = useCallback(
    (sectionId: string | undefined, athleteId: string) => {
      setAthleteSession((prev) => {
        const prevSession = prev[athleteId];
        return {
          ...prev,
          [athleteId]: {
            workoutId: prevSession?.workoutId,
            workoutName: prevSession?.workoutName,
            sectionId,
          },
        };
      });
    },
    [setAthleteSession],
  );

  const getSelectedSectionId = useCallback(
    (athleteId: string) => athleteSession[athleteId]?.sectionId,
    [athleteSession],
  );

  const getSelectedWorkoutId = useCallback(
    (athleteId: string) => athleteSession[athleteId]?.workoutId,
    [athleteSession],
  );

  const onSetSplitView = useCallback(
    (enabled: boolean) => {
      setSplitViewEnabled(enabled);

      // When switching to single view, select the first athlete if none is selected
      if (!enabled && selectedAthletes.length > 0 && selectedAthletes[0]) {
        setActiveAthletes([selectedAthletes[0]]);
      } else if (enabled) {
        setActiveAthletes(selectedAthletes);
      }
    },
    [setSplitViewEnabled, selectedAthletes, setActiveAthletes],
  );

  const isAthleteActive = useCallback(
    (athleteId: string) => activeAthleteIds.includes(athleteId),
    [activeAthleteIds],
  );

  const cleanUpTabletSession = useCallback(() => {
    setSplitViewEnabled(false);
    setActiveAthletes([]);
    setAthleteSession({});
    onExit();
  }, [setSplitViewEnabled, setActiveAthletes, setAthleteSession, onExit]);

  const removeAthleteFromSelectedAthletes = useCallback(
    (athleteId: string) => {
      // Remove athlete from session
      setAthleteSession((prev) => {
        const newSession = { ...prev };
        delete newSession[athleteId];
        return newSession;
      });

      // Remove athlete from selected list
      deselectAthlete(athleteId);

      if (activeAthleteIds.includes(athleteId)) {
        const remainingAthletes = selectedAthletes.filter(
          (athlete) => athlete.id !== athleteId,
        );
        
        if (splitViewEnabled && remainingAthletes.length > 1) {
          setActiveAthletes(remainingAthletes);
        } else if (remainingAthletes.length >= 1 && remainingAthletes[0] ) {
          setActiveAthletes([remainingAthletes[0]]);
        } else {
          cleanUpTabletSession();
        }
      }
    },
    [
      setAthleteSession,
      activeAthleteIds,
      deselectAthlete,
      selectedAthletes,
      setActiveAthletes,
      splitViewEnabled,
      cleanUpTabletSession,
    ],
  );

  const contextValue = useMemo(
    () => ({
      selectedAthletes,
      removeAthleteFromSelectedAthletes,
      activeAthletes,
      activeAthleteIds,
      setActiveAthletes,
      isAthleteActive,
      athleteSession,
      getSelectedSectionId,
      getSelectedWorkoutId,
      onSelectWorkout,
      onSelectSection,
      splitViewEnabled,
      canShowSplitView,
      onSetSplitView,
      onExit: cleanUpTabletSession,
    }),
    [
      selectedAthletes,
      removeAthleteFromSelectedAthletes,
      activeAthletes,
      activeAthleteIds,
      setActiveAthletes,
      isAthleteActive,
      athleteSession,
      getSelectedSectionId,
      getSelectedWorkoutId,
      onSelectWorkout,
      onSelectSection,
      splitViewEnabled,
      canShowSplitView,
      onSetSplitView,
      cleanUpTabletSession,
    ],
  );

  return (
    <TabletContext.Provider value={contextValue}>
      {children}
    </TabletContext.Provider>
  );
};

export const useTabletContext = (): TabletContextValue => {
  const context = useContext(TabletContext);
  if (!context) {
    throw new Error('useTabletContext must be used within a TabletProvider');
  }
  return context;
};
